<template>
  <v-container fluid class="pa-0">
    <Toolbar />

    <!-- 🔹 Profile Grid -->
    <v-row dense class="mt-5">
      <v-col v-for="(p, i) in profiles" :key="i" cols="12" sm="6" md="4" lg="3">
        <ProfileCard
          :profile="p"
          @view="viewProfile"
          @like="likeProfile"
          @share="shareProfile"
          :onlineUserIds="onlineUserIds"
        />
      </v-col>
    </v-row>

    <!-- 🔄 Loading indicator -->
    <v-row justify="center" v-if="loadingMore" class="my-6">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-row>

    <!-- 🚫 No matches -->
    <v-row
      v-if="!loading && profiles.length === 0 && noMoreResults"
      align="center"
      justify="center"
      class="text-center my-10"
    >
      <v-col cols="12" md="6">
        <v-img
          src="@/assets/no-results.png"
          height="140"
          contain
          class="mb-3"
        ></v-img>
        <h3 class="font-weight-bold mb-2">No Matches Found</h3>
        <p class="text-body-2 grey--text mb-4">
          Try relaxing or changing your matchmaking preferences to find more
          profiles.
        </p>
        <v-btn
          color="primary"
          rounded
          @click="$router.push('/user/matchmaking-preferences')"
        >
          Adjust Preferences
        </v-btn>
      </v-col>
    </v-row>

    <!-- 🟢 End of list -->
    <v-row
      v-if="!loadingMore && profiles.length > 0 && noMoreResults"
      justify="center"
      class="my-8 text-center"
    >
      <v-col cols="12" md="6">
        <v-divider class="my-4"></v-divider>
        <p class="text-body-2 grey--text">
          You’ve reached the end of current matches. 💚<br />
          Broaden your preferences to see more profiles.
        </p>
        <v-btn
          small
          rounded
          outlined
          color="primary"
          class="mt-2"
          @click="$router.push('/user/matchmaking-preferences')"
        >
          Broaden Preferences
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import ProfileCard from "@/modules/Home/components/ProfileCard.vue";
import Toolbar from "@/modules/Layout/components/Toolbar.vue";
import homeService from "@/modules/Home/service.js";
import { mapGetters } from "vuex";

export default {
  components: { ProfileCard, Toolbar },

  data() {
    return {
      profiles: [],
      page: 1,
      limit: 12,
      offset: 0,
      loading: false,
      loadingMore: false,
      noMoreResults: false,
    };
  },

  async created() {
    await this.validateUserStatus();
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },

  computed: {
    ...mapGetters("global", ["onlineUserIds", "currentUser"]),
  },

  methods: {
    // ✅ Validate preferences before fetching matches
    async validateUserStatus() {
      const user = this.currentUser;
      if (!user) return;

      if (!user.isPreferenceCompleted) {
        this.$swal({
          title: "Complete Your Matchmaking Preferences",
          html: `
            <p style="font-size:15px;">
              To receive accurate and halal match recommendations,
              please complete your matchmaking preferences first.
            </p>
            <p style="font-size:13px; color:#666; margin-top:10px;">
              “And one of His signs is that He created for you mates from among yourselves, 
              that you may dwell in tranquility with them.” 
              <br><span style="font-size:12px;">(Qur'an 30:21)</span>
            </p>
          `,
          icon: "info",
          confirmButtonText: "Go to Preferences",
          allowOutsideClick: false,
          confirmButtonColor: "#2A6465",
        }).then((res) => {
          if (res.isConfirmed) {
            this.$router.push("/user/matchmaking-preferences");
          }
        });
        return;
      }

      await this.loadProfiles();
    },

    // ✅ Load match list
    async loadProfiles() {
      if (this.loadingMore || this.noMoreResults) return;

      this.loadingMore = true;
      this.loading = this.page === 1;

      try {
        const res = await homeService.findMatch({
          limit: this.limit,
          offset: this.offset,
          minScore: 50, // ✅ anything below 50% won’t appear
        });

        const data = res?.data || [];
        if (!data.length) {
          if (this.page === 1) this.noMoreResults = true;
          else this.noMoreResults = true;
        } else {
          // Merge new batch
          this.profiles.push(...data);
          this.offset += this.limit;
          this.page++;
        }
      } catch (err) {
        console.error("Error loading profiles:", err);
      } finally {
        this.loadingMore = false;
        this.loading = false;
      }
    },

    // ✅ Infinite scroll
    handleScroll() {
      const nearBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 400;
      if (nearBottom && !this.loadingMore && !this.noMoreResults) {
        this.loadProfiles();
      }
    },

    // ✅ Actions
    viewProfile(profile) {
      this.$router.push({
        name: "public-profile-view",
        params: { profileId: profile.id },
        query: { view: true },
      });
    },
    async likeProfile(profile) {
      try {
        await homeService.likeProfile(profile.id);
        profile.likeCount = (profile.likeCount || 0) + 1;
      } catch (err) {
        console.error(err);
      }
    },
    shareProfile(profile) {
      const shareUrl = `${window.location.origin}/profile/${profile.id}`;
      if (navigator.share) {
        navigator.share({
          title: `${profile.name} - Nikah Connect`,
          text: `${profile.name}, ${profile.city}, ${profile.state}`,
          url: shareUrl,
        });
      } else {
        navigator.clipboard.writeText(shareUrl);
        this.$swal({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Profile link copied!",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    },
  },
};
</script>

<style scoped>
.v-container {
  min-height: 100vh;
}
.text-caption {
  font-size: 12px;
}
</style>